
import React from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Play, FileText, GamepadIcon, PenTool } from 'lucide-react';

interface ActivityMention {
  type: 'test' | 'game' | 'worksheet' | 'writing';
  subject?: string;
  grade?: string;
  category?: string;
  topic?: string;
  context: string;
}

interface ActivityResponseParserProps {
  content: string;
  onActivityAction: (actionData: any) => void;
  userGrade: string;
}

export const ActivityResponseParser: React.FC<ActivityResponseParserProps> = ({
  content,
  onActivityAction,
  userGrade
}) => {
  const parseActivityMentions = (text: string): ActivityMention[] => {
    const mentions: ActivityMention[] = [];
    
    // Patterns to detect activity mentions
    const testPatterns = [
      /(?:take|try|practice)\s+(?:a\s+)?(?:test|quiz|assessment)(?:\s+(?:on|in|for)\s+([a-zA-Z\s]+))?/gi,
      /(?:math|english|science|reading|writing)\s+(?:test|quiz|practice)/gi,
      /practice\s+questions?\s+(?:on|in|for)\s+([a-zA-Z\s]+)/gi
    ];

    const gamePatterns = [
      /(?:play|try)\s+(?:a\s+)?(?:game|games)(?:\s+(?:on|in|for)\s+([a-zA-Z\s]+))?/gi,
      /(?:math|english|science|reading)\s+(?:game|games)/gi,
      /educational\s+games?/gi
    ];

    const worksheetPatterns = [
      /(?:practice|work\s+on|try)\s+(?:a\s+)?(?:worksheet|worksheets)(?:\s+(?:on|in|for)\s+([a-zA-Z\s]+))?/gi,
      /(?:math|english|science|reading)\s+(?:worksheet|worksheets)/gi,
      /practice\s+(?:sheet|sheets)/gi
    ];

    const writingPatterns = [
      /(?:practice|work\s+on|try)\s+(?:writing|essay|composition)/gi,
      /writing\s+(?:practice|exercise)/gi,
      /(?:essay|story|composition)\s+writing/gi
    ];

    // Check for test mentions
    testPatterns.forEach(pattern => {
      const matches = [...text.matchAll(pattern)];
      matches.forEach(match => {
        mentions.push({
          type: 'test',
          subject: match[1] || 'general',
          grade: userGrade,
          context: match[0]
        });
      });
    });

    // Check for game mentions
    gamePatterns.forEach(pattern => {
      const matches = [...text.matchAll(pattern)];
      matches.forEach(match => {
        mentions.push({
          type: 'game',
          subject: match[1] || 'general',
          grade: userGrade,
          context: match[0]
        });
      });
    });

    // Check for worksheet mentions
    worksheetPatterns.forEach(pattern => {
      const matches = [...text.matchAll(pattern)];
      matches.forEach(match => {
        mentions.push({
          type: 'worksheet',
          subject: match[1] || 'general',
          grade: userGrade,
          context: match[0]
        });
      });
    });

    // Check for writing mentions
    writingPatterns.forEach(pattern => {
      const matches = [...text.matchAll(pattern)];
      matches.forEach(match => {
        mentions.push({
          type: 'writing',
          grade: userGrade,
          context: match[0]
        });
      });
    });

    return mentions;
  };

  const handleActivityClick = (mention: ActivityMention) => {
    const actionData = {
      subjectName: mention.subject || 'Mathematics',
      gradeName: mention.grade || userGrade,
      categoryId: mention.category,
      topicId: mention.topic
    };

    onActivityAction({
      type: mention.type,
      actionData
    });
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'test': return <FileText className="h-4 w-4" />;
      case 'game': return <GamepadIcon className="h-4 w-4" />;
      case 'worksheet': return <FileText className="h-4 w-4" />;
      case 'writing': return <PenTool className="h-4 w-4" />;
      default: return <Play className="h-4 w-4" />;
    }
  };

  const getActivityColor = (type: string) => {
    switch (type) {
      case 'test': return 'bg-blue-100 text-blue-700 hover:bg-blue-200';
      case 'game': return 'bg-green-100 text-green-700 hover:bg-green-200';
      case 'worksheet': return 'bg-purple-100 text-purple-700 hover:bg-purple-200';
      case 'writing': return 'bg-orange-100 text-orange-700 hover:bg-orange-200';
      default: return 'bg-gray-100 text-gray-700 hover:bg-gray-200';
    }
  };

  const mentions = parseActivityMentions(content);
  
  // Remove duplicates
  const uniqueMentions = mentions.filter((mention, index, self) => 
    index === self.findIndex(m => m.type === mention.type && m.subject === mention.subject)
  );

  if (uniqueMentions.length === 0) {
    return null;
  }

  return (
    <div className="mt-4 space-y-2">
      <p className="text-sm font-medium text-muted-foreground">Quick Actions:</p>
      <div className="flex flex-wrap gap-2">
        {uniqueMentions.map((mention, index) => (
          <Button
            key={index}
            variant="outline"
            size="sm"
            onClick={() => handleActivityClick(mention)}
            className={`${getActivityColor(mention.type)} border-0 transition-all duration-200`}
          >
            {getActivityIcon(mention.type)}
            <span className="ml-1 capitalize">
              {mention.type} {mention.subject && mention.subject !== 'general' ? `- ${mention.subject}` : ''}
            </span>
          </Button>
        ))}
      </div>
    </div>
  );
};
